// Dental domain constants + helpers for DentalCAD Connect (Fase D3).
// Tooth state is stored keyed by FDI number (canonical) so switching the display
// numbering system (FDI <-> Universal) never changes the stored data.

// Restoration types. `common: true` => shown in the short list; the rest live behind "view more".
export const RESTORATION_TYPES = [
  { value: "crown", common: true },
  { value: "bridge", common: true },
  { value: "veneer", common: true },
  { value: "inlay", common: true },
  { value: "onlay", common: true },
  { value: "implant_crown", common: true },
  { value: "implant_bridge" },
  { value: "full_arch" },
  { value: "wax_up" },
  { value: "surgical_guide" },
  { value: "custom_abutment" },
  { value: "temp_crown" },
  { value: "temp_bridge" },
  { value: "maryland_bridge" },
  { value: "night_guard" },
  { value: "model_only" },
  { value: "gingival_mask" },
  { value: "custom_tray" },
  { value: "other" },
];

export const MATERIALS = [
  { value: "zirconia", common: true },
  { value: "emax", common: true },
  { value: "pfm", common: true },
  { value: "pmma", common: true },
  { value: "titanium", common: true },
  { value: "composite", common: true },
  { value: "cast_metal" },
  { value: "cocr" },
  { value: "wax" },
  { value: "printed_resin" },
  { value: "peek" },
  { value: "hybrid_ceramic" },
  { value: "feldspathic" },
  { value: "other" },
];

// Types that require the "Implant information" section.
export const IMPLANT_TYPES = ["implant_crown", "implant_bridge", "custom_abutment"];
export const isImplantType = (type) => IMPLANT_TYPES.includes(type);

// ---- Tooth layout (canonical FDI) ----
// Displayed left -> right as the clinician faces the patient.
export const UPPER_FDI = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
export const LOWER_FDI = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

// FDI -> Universal (1-32) mapping, position-preserving.
const _UNIVERSAL_UPPER = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
const _UNIVERSAL_LOWER = [38, 37, 36, 35, 34, 33, 32, 31, 41, 42, 43, 44, 45, 46, 47, 48];
export const FDI_TO_UNIVERSAL = (() => {
  const map = {};
  _UNIVERSAL_UPPER.forEach((fdi, i) => { map[fdi] = i + 1; });
  _UNIVERSAL_LOWER.forEach((fdi, i) => { map[fdi] = i + 17; });
  return map;
})();

export function toothLabel(fdi, numbering = "fdi") {
  return numbering === "universal" ? String(FDI_TO_UNIVERSAL[fdi] ?? fdi) : String(fdi);
}

export const TOOTH_STATES = ["abutment", "pontic"];
// Cycle: none -> abutment -> pontic -> none
export function nextToothState(current) {
  if (!current) return "abutment";
  if (current === "abutment") return "pontic";
  return null;
}

export function emptyImplant() {
  return { retainedBy: "", brand: "", connection: "", tiBase: "", scanbody: "" };
}

// A single blank restoration (frontend working shape).
export function emptyRestoration() {
  return {
    localId: Math.random().toString(36).slice(2),
    type: "crown",
    material: "zirconia",
    shade: "",
    clinicalInstructions: "",
    teeth: {}, // FDI(string) -> 'abutment' | 'pontic'
    cadFiles: [], // File[] (new uploads)
    existingCad: [], // [{url,name}] (already uploaded, when editing)
    implant: emptyImplant(),
  };
}

// Best-effort parse of a legacy "11, 21" tooth string into an FDI-keyed map.
export function parseLegacyTeeth(str = "") {
  const teeth = {};
  String(str)
    .split(/[,;/\s]+/)
    .map((s) => s.replace(/[^0-9]/g, ""))
    .filter((n) => n.length === 2 && FDI_TO_UNIVERSAL[Number(n)])
    .forEach((n) => { teeth[n] = "abutment"; });
  return teeth;
}

// Ordered list of selected FDI teeth for a restoration (for chips / summaries).
export function teethList(teeth = {}) {
  const order = [...UPPER_FDI, ...LOWER_FDI];
  return order
    .filter((fdi) => teeth[String(fdi)])
    .map((fdi) => ({ fdi, state: teeth[String(fdi)] }));
}

export function teethLabelString(teeth = {}, numbering = "fdi") {
  return teethList(teeth).map(({ fdi }) => toothLabel(fdi, numbering)).join(", ");
}

// Normalized list of restorations for a case (new array + legacy flat fallback).
export function getCaseRestorations(caseItem) {
  if (Array.isArray(caseItem?.restorations) && caseItem.restorations.length) {
    return caseItem.restorations;
  }
  return null; // signals "legacy case, render flat fields"
}
